import React, { useEffect, useState } from 'react';
import CityPicker from './CityPicker';
import portalService from '../services/portalService';

const LOCATION_KEY = 'pr.userLocation';

function readLocation() {
  try {
    const raw = localStorage.getItem(LOCATION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Where a club lives: GLOBAL (belongs everywhere, listed in no City Portal) or a
 * single home town, which is what puts it in that town's CLUBS IN TOWN card.
 *
 * Shared by the create page and the club's settings so the two can't drift.
 * Reports both halves at once — `onChange({ isGlobal, city })` — because the
 * backend writes them together: going global clears the town and its coords.
 */
export default function ClubLocationField({ isGlobal, city, onChange }) {
  const [cities, setCities] = useState([]);

  // Same public catalog the signal composer and Portal use for the nearest-city
  // quick-pick; the Mapbox search inside CityPicker covers everywhere else.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await portalService.listCities();
      if (!cancelled && r.success) setCities(r.data || []);
    })();
    return () => { cancelled = true; };
  }, []);

  const options = [
    { value: true, label: 'GLOBAL', hint: 'Anywhere. Not tied to a town.' },
    { value: false, label: 'A TOWN', hint: 'Shows up in that City Portal.' },
  ];

  return (
    <div className="border-2 border-black">
      <div className="border-b-2 border-black px-4 py-3 font-mono text-xs font-bold uppercase tracking-wider text-black">
        WHERE THE CLUB LIVES
      </div>
      <div className="space-y-4 p-4">
        <div className="inline-flex border-2 border-black">
          {options.map((opt, idx) => (
            <button
              key={opt.label}
              type="button"
              onClick={() => onChange({ isGlobal: opt.value, city: opt.value ? null : city })}
              className={`px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-wider transition-colors ${
                idx > 0 ? 'border-l-2 border-black' : ''
              } ${isGlobal === opt.value ? 'bg-black text-white' : 'bg-white text-black hover:bg-gray-100'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <p className="font-mono text-[11px] text-gray-500">
          {isGlobal
            ? 'A global club belongs everywhere — it won’t be listed under CLUBS IN TOWN on any City Portal.'
            : 'Members of this town see the club under CLUBS IN TOWN on their City Portal.'}
        </p>

        {!isGlobal && (
          <CityPicker
            value={city}
            onChange={(c) => onChange({ isGlobal: false, city: c })}
            cities={cities}
            nearLocation={readLocation()}
          />
        )}
      </div>
    </div>
  );
}
